// ─────────────────────────────────────────────────────────────────────────────
// Primary navigation information architecture.
//
// LAST REVIEWED
//   2026-10-08 — synced to the October build: Case Intelligence Studio,
//   clients & portal, Documents, Command Center, Privacy & Data Protection,
//   Deep Audit, due-diligence rooms, audit readiness and AI Administration
//   added; Workspaces, Collaboration ("@mentions"), Pre-built agents, the
//   template library and the glossary removed (unbuilt, or India-only and
//   404 here); persona descs that promised white-label or SCIM corrected
//   (both are off on every global plan). Count recomputed: FEATURES.length.
//   2026-09-18 — the September work layer (meetings, tasks, lifecycle, blog,
//   mobile) added to the Platform menu; Roadmap and Changelog restored to
//   Resources as they become real pages; feature count recomputed from the
//   catalogue (`FEATURES.length`, i.e. entries with no `market` tag).
//
// THE ONE RULE FOR THIS FILE
//   Every `href` below must resolve to a route that exists in src/app.
//   A menu item that 404s is worse than an absent menu item.
//
// SOURCES OF TRUTH (all hrefs here were checked against these on 2026-08-19)
//   /product/*         → src/app/product/*/page.tsx                (5 modules)
//   /solutions/*       → src/app/solutions/*/page.tsx              (10 personas)
//   /practice-areas/*  → src/app/practice-areas/_data/areas.ts     (13 areas)
//   /features/*        → src/app/features/_data/features.ts        (83 features)
//   everything else    → a top-level src/app/<segment>/page.tsx
//
//   The feature and practice-area catalogues are NOT imported here on purpose:
//   the navbar is a client component rendered on every page, and pulling in
//   features.ts + areas.ts would ship ~120KB of prose (FAQs, statute tables,
//   how-it-works copy) to every visitor just to render link labels. Labels are
//   therefore duplicated. If you rename a feature or retire a slug, update this
//   file in the same change set.
//
// PANEL GEOMETRY IS DERIVED, NOT AUTHORED
//   Panels used to carry a hand-tuned `width` and `cols`. Six hand-tuned widths
//   produced six different column widths (189px–214px), which is a large part
//   of why the menus read as six unrelated designs. Both are now computed:
//   column count = columns.length + (featured ? 1 : 0), and the panel width
//   falls out of that in CSS from a single --mega-col-w token. Every panel in
//   the bar therefore has identical column widths. Do not reintroduce `width`.
//
// COPY LENGTH IS A LAYOUT CONSTRAINT
//   `desc` must fit one line at --mega-col-w. That is ~34 characters. Longer
//   strings wrap to two lines, which makes row heights uneven and column
//   bottoms ragged — a large part of why the old panels looked unfinished.
//   Keep every desc traceable to the page it links to; never invent a claim to
//   fill the line, and never pad a short one.
//
// DELIBERATE OMISSIONS
//   • /pricing — hidden by decision; see the note in Navbar.tsx and the
//     matching comment in src/app/sitemap.ts.
//   • Webinars, Reports, Case Studies, Guides & Playbooks, Documentation,
//     a legal-AI benchmark page and a site search — requested during the nav
//     redesign, but no such routes exist. They stay out until they do.
//   • /api, /customers, /use-cases — routes exist but each page is a
//     noindexed "Coming soon" placeholder with no real content (nav-audit
//     2026-08-23). Same rule as above, applied to routes that were scaffolded
//     ahead of their content instead of not existing yet. Re-add once each
//     has something real behind it. /roadmap and /changelog left this list on
//     2026-09-18 as they became real pages.
//   • /tools, /calendar, /practice-areas, /product/tax-ai, /solutions/ca-cs —
//     real, live, India-only content (GST/TDS/stamp duty/ROC/income tax
//     calculators, statutory deadlines, practice-area hubs); noindexed and
//     kept out of the global nav for the same reason as India-tagged features
//     elsewhere on this site (see market: "IN" in features/_data/features.ts).
//     Not a placeholder — genuinely useful, just not this menu's job to
//     promote to a global audience.
// ─────────────────────────────────────────────────────────────────────────────

export type MenuId =
  | "platform"
  | "solutions"
  | "intelligence"
  | "resources"
  | "company";

/** Icon keys, resolved to inline SVG by ICONS in Navbar.tsx. */
export type NavIcon =
  | "overview"
  | "legal"
  | "compliance"
  | "tax"
  | "agents"
  | "global";

export type NavLink = {
  label: string;
  href: string;
  /** One short line, <= ~34 chars. Must be traceable to the page it links to. */
  desc?: string;
  /** Honest status flag — mirrors `status` in features.ts. */
  tag?: "Beta" | "Soon";
  /** Copper-toned link, used for the "browse everything" escape hatches. */
  accent?: boolean;
  /** Only rendered inside a spotlight column. */
  icon?: NavIcon;
};

export type NavColumn = {
  title: string;
  /**
   * Marks the column a reader should land on first. It gets icon tiles, a
   * heavier label and a dividing rule. At most one per menu — a panel with two
   * focal points has none.
   */
  spotlight?: boolean;
  links: ReadonlyArray<NavLink>;
};

export type NavMenu = {
  id: MenuId;
  label: string;
  /** Announced to screen readers on the trigger button. */
  hint: string;
  /** Path prefixes that light the trigger up as the active section. */
  activePrefixes: ReadonlyArray<string>;
  /**
   * "bar"   — panel is centred on the navbar itself (wide panels; cannot spill
   *           off-screen however far left or right the trigger sits).
   * "right" — panel hangs off the right edge of its trigger (narrow panels on
   *           the right of the bar).
   */
  anchor: "bar" | "right";
  columns: ReadonlyArray<NavColumn>;
  featured?: {
    eyebrow: string;
    line: string;
    cta: NavLink;
    secondary?: NavLink;
  };
  footer?: {
    link: NavLink;
    tag?: string;
  };
};

export const MENUS: ReadonlyArray<NavMenu> = [
  // ── 1. PLATFORM ───────────────────────────────────────────────────────────
  {
    id: "platform",
    label: "Platform",
    hint: "Platform modules and core capabilities",
    activePrefixes: ["/product", "/integrations", "/security"],
    anchor: "bar",
    columns: [
      {
        title: "Modules",
        spotlight: true,
        links: [
          { label: "Platform overview", href: "/product", desc: "Three modules, one login", icon: "overview" },
          { label: "Legal AI", href: "/product/legal-ai", desc: "Review, redlining, research", icon: "legal" },
          { label: "Compliance AI", href: "/product/compliance-ai", desc: "Regulatory monitoring & alerts", icon: "compliance" },
          { label: "Workflow Agents", href: "/product/workflow-agents", desc: "No-code automation", icon: "agents" },
        ],
      },
      {
        title: "Legal work",
        links: [
          { label: "AI legal assistant", href: "/features/vio-ask", desc: "Cited Q&A on any matter" },
          { label: "AI drafting", href: "/features/vio-draft", desc: "Replacement language, in Word" },
          { label: "Contract review", href: "/features/contract-scanner", desc: "PDF, DOCX and scanned files" },
          { label: "AI redlining", href: "/features/ai-redlining", desc: "Tracked changes, ready to send" },
          // "Legal research" → /features/court-research-corpus removed
          // 2026-09-05: the corpus behind it is Indian and `/research` is
          // mounted `require_india_edition`, so that page is 404 here.
          { label: "Case Intelligence Studio", href: "/features/case-intelligence-studio", desc: "Verified facts, issues, gaps" },
          { label: "Governing law detection", href: "/features/governing-law-detection", desc: "The clause decides, not a guess" },
        ],
      },
      // 2026-10-08: "Workspaces" ("isolated workspaces") and "Collaboration"
      // ("comments, @mentions") removed — neither described anything built;
      // both feature pages now say so. Clients & portal and Documents added.
      {
        title: "Workspace",
        links: [
          { label: "Matter intelligence", href: "/features/matter-management", desc: "Documents, tasks, a timeline" },
          { label: "Clients & portal", href: "/features/client-portal", desc: "Scoped shares, magic-link sign-in" },
          { label: "Documents & Matter File", href: "/features/documents", desc: "Category decides what runs" },
          { label: "Legal knowledge base", href: "/features/nexus-search", desc: "Semantic search on the vault" },
          { label: "Encrypted vault", href: "/features/encrypted-vault", desc: "AES-256 encryption at rest" },
          { label: "Approval workflows", href: "/features/approval-workflows", desc: "Human-in-the-loop gates", tag: "Beta" },
        ],
      },
      // 2026-10-08: "Pre-built agents" removed — every seeded agent template
      // is tagged for India, so the library is empty on this product and the
      // feature page is withheld (404).
      {
        title: "Privacy & trust",
        links: [
          { label: "Privacy & Data Protection", href: "/features/privacy-readiness", desc: "14 privacy laws, one programme", tag: "Beta" },
          { label: "Custom agents", href: "/features/custom-agents", desc: "Triggers, conditions, actions", tag: "Beta" },
          { label: "Integrations", href: "/integrations", desc: "Where LexVio meets your stack" },
          { label: "Security & privacy", href: "/security", desc: "Encryption, residency, access" },
          { label: "Trust centre", href: "/trust", desc: "Security posture and certification status" },
        ],
      },
      // The work layer shipped in September 2026 (Todo, Meeting Intelligence,
      // contract lifecycle, the community blog, the phone app) — all mounted
      // with no plan gate and no edition gate, so every one is a real page on
      // this product. A fifth column is the widest panel the bar carries (see
      // the Intelligence menu), so the featured card gave way to it:
      // "Explore the platform" is the first row of the Modules column and
      // "Scan a contract free" is the Resources menu's featured CTA.
      {
        title: "Work",
        links: [
          { label: "Meeting Intelligence", href: "/features/meeting-intelligence", desc: "Record → transcript → minutes → tasks" },
          { label: "Tasks", href: "/features/tasks", desc: "One list, Vio-drafted, matter-linked" },
          { label: "Command Center", href: "/features/command-center", desc: "What needs you, on one screen" },
          { label: "Contract lifecycle", href: "/features/contract-lifecycle", desc: "Status, expiry, notices, versions" },
          { label: "Write for the blog", href: "/features/community-blog", desc: "Open to every account" },
          { label: "Mobile app", href: "/features/mobile-app", desc: "In testing — not on the stores yet", tag: "Soon" },
        ],
      },
    ],
    footer: {
      link: { label: "Browse all 83 features", href: "/features", accent: true },
      tag: "3 modules · 1 login",
    },
  },

  // ── 2. SOLUTIONS ──────────────────────────────────────────────────────────
  {
    id: "solutions",
    label: "Solutions",
    hint: "Solutions by firm type and function",
    activePrefixes: ["/solutions"],
    anchor: "bar",
    columns: [
      {
        title: "Legal practice",
        spotlight: true,
        links: [
          { label: "Law firms", href: "/solutions/law-firms", desc: "Matters, clients, case analysis" },
          { label: "Litigators", href: "/solutions/advocates", desc: "Facts, orders, bundles, drafting" },
        ],
      },
      {
        title: "In-house & enterprise",
        links: [
          { label: "Enterprise & MNC", href: "/solutions/enterprise", desc: "Roles, access policies, CLM" },
          { label: "Financial institutions", href: "/solutions/financial-institutions", desc: "Regulatory & audit-ready" },
          { label: "Small businesses", href: "/solutions/smb", desc: "Vendor, lease and job contracts" },
        ],
      },
      {
        title: "Founders & independents",
        links: [
          { label: "Founders", href: "/solutions/founders", desc: "SAFE notes, term sheets" },
          { label: "Agencies", href: "/solutions/agencies", desc: "MSAs, SOWs, retainers" },
          { label: "Freelancers", href: "/solutions/freelancers", desc: "Client contracts, IP ownership" },
          { label: "Law students", href: "/solutions/students", desc: "Discounted access" },
        ],
      },
    ],
    featured: {
      eyebrow: "Solutions",
      line: "Built for the way legal teams actually work — from a solo practice to a 500-seat enterprise.",
      cta: { label: "Compare all solutions", href: "/solutions" },
      secondary: { label: "Book a demo", href: "/book-demo", desc: "See it on your own documents" },
    },
    footer: {
      link: { label: "See every persona", href: "/solutions", accent: true },
      tag: "9 tailored builds",
    },
  },

  // ── 3. INTELLIGENCE ───────────────────────────────────────────────────────
  // NOTE: the India build's "Practice areas" menu (13 hubs, every one keyed to
  // an Indian statute — Companies Act 2013, ICA 1872, DPDP Act 2023, the four
  // labour codes, stamp duty by state) was removed here rather than relabelled.
  // Its own footer tag admitted "India-specific"; there is no global content
  // behind any of those 13 pages yet, and a nav item that links to India-only
  // content from a global-branded site is exactly the §1A leak shape the
  // backend spent real effort closing. Reintroduce as a real global menu only
  // once practice-area hubs have global (or per-jurisdiction) content.
  // Consolidation of the brief's "AI Capabilities" + "Legal Intelligence"
  // menus. Seven triggers do not fit the bar (see NAV DENSITY in globals.css);
  // both groups live here as labelled column sets instead.
  //
  // This is the only menu without a featured card: five column sets already
  // fill the widest panel the bar can carry, and a sixth cell would push every
  // column below a readable width.
  {
    id: "intelligence",
    label: "Intelligence",
    hint: "AI capabilities and legal intelligence",
    activePrefixes: ["/features"],
    anchor: "bar",
    columns: [
      {
        title: "Research & reasoning",
        spotlight: true,
        links: [
          // Legal research AI, Citation intelligence and Legal simulators
          // removed 2026-09-05 — all three are India-only backend surfaces
          // (`/research` and the simulator pack, mounted
          // `require_india_edition`), so those pages are 404 here and this
          // file's one rule is that no href may 404. Replaced with the two
          // checks that do run on this product.
          { label: "Citation verification", href: "/features/citation-verification", desc: "A verdict, not just a link" },
          { label: "Governing law detection", href: "/features/governing-law-detection", desc: "Which law actually applies" },
          { label: "Statute verification", href: "/features/statute-verification", desc: "Checked at the official source", tag: "Soon" },
          { label: "Knowledge graph", href: "/features/nexus-graph", desc: "How documents and parties relate", tag: "Beta" },
        ],
      },
      {
        title: "Contract & document AI",
        links: [
          { label: "Contract AI", href: "/features/clause-risk-scoring", desc: "Red / amber / green per clause" },
          { label: "Legal Health Score", href: "/features/legal-health-score", desc: "One 0-100 number, per contract" },
          { label: "Document AI", href: "/features/bulk-analysis", desc: "One question, thousands of files" },
          { label: "Drafting AI", href: "/features/ai-redlining", desc: "Tracked-change Word output" },
          { label: "Deep Audit", href: "/features/deep-audit", desc: "Three models, one report" },
          { label: "Fix suggestions", href: "/features/fix-suggestions", desc: "Replacement language per flag" },
        ],
      },
      {
        title: "Portfolio intelligence",
        links: [
          { label: "Portfolio search", href: "/features/nexus-search", desc: "Semantic + keyword on the vault" },
          { label: "Portfolio Q&A", href: "/features/nexus-ask", desc: "Synthesised answers, with cites" },
          { label: "Clause coverage map", href: "/features/nexus-clause-coverage", desc: "Present, missing, non-standard" },
          { label: "Negotiation rollups", href: "/features/nexus-negotiation-rollup", desc: "Where you land on terms", tag: "Beta" },
          { label: "Insights feed", href: "/features/nexus-insights", desc: "Alerts when the portfolio moves", tag: "Beta" },
        ],
      },
      {
        title: "Market & regulatory",
        links: [
          // Regulator monitoring and Regulatory change feed removed
          // 2026-09-05 — `/api/v1/compliance` (alerts, calendar, reg-change)
          // is mounted `require_india_edition` and `compliance_calendar` /
          // `reg_change_impact` are both in INDIA_ONLY_FEATURE_KEYS, so those
          // pages are 404 here.
          { label: "M&A deal intelligence", href: "/features/m-and-a-intelligence", desc: "Data-room diligence, red flags" },
          { label: "Procurement hub", href: "/features/procurement-hub", desc: "Vendor risk-tiering, approvals" },
          { label: "Due-diligence rooms", href: "/features/due-diligence-rooms", desc: "Bundle, aggregate, synthesise" },
          { label: "Audit readiness", href: "/features/audit-readiness", desc: "SOC 2 controls vs your policies" },
        ],
      },
      {
        title: "AI controls",
        links: [
          { label: "Prompt injection defence", href: "/features/prompt-injection-defence", desc: "Pasted text is sanitised" },
          { label: "No training on your data", href: "/features/no-training-on-your-data", desc: "No training programme at all" },
          { label: "AI Administration", href: "/features/ai-administration", desc: "Model ceiling, spend cap, audit" },
          { label: "Cost guardrails", href: "/features/cost-guardrails", desc: "Monthly quota, hard stop" },
          { label: "Activity audit log", href: "/features/audit-log", desc: "Immutable record of actions" },
        ],
      },
    ],
    footer: {
      link: { label: "Browse all 83 features", href: "/features", accent: true },
      tag: "AI that supports legal judgment — it does not replace it",
    },
  },

  // ── 4. RESOURCES ──────────────────────────────────────────────────────────
  // Intelligence API (/api), Customer stories and Use cases were dropped from
  // here (nav-audit 2026-08-23) — noindexed "Coming soon" placeholder pages
  // with no real content behind them yet (confirmed on each page itself, not
  // just inferred). That's the same "stays out until it exists" rule this
  // file's own header comment already applies to Pricing/Webinars/Reports/etc.
  // — they just had routes already scaffolded, which let them slip past that
  // rule. Re-add once there's real content; /api is also gone from the
  // Platform menu's "Agents & trust" column for the same reason. Roadmap and
  // Changelog were in that list until 2026-09-18, when both became real pages.
  //
  // The featured CTA used to point at /tools ("Open the free tools") — /tools
  // is 100% India-only calculators (GST, TDS, stamp duty, ROC, income tax)
  // and is noindexed for the global site; it was also this menu's single
  // biggest promotional slot. Repointed at the free scanner instead, which is
  // both genuinely global and already the site's proven primary free-tool CTA
  // (homepage hero, Platform menu's own featured secondary).
  {
    id: "resources",
    label: "Resources",
    hint: "Free tools, guides and product updates",
    activePrefixes: ["/blog", "/faqs", "/status", "/scan", "/roadmap", "/changelog"],
    anchor: "bar",
    columns: [
      {
        title: "Free to use",
        spotlight: true,
        links: [
          // "Template library" (/library) and "Legal glossary" (/glossary)
          // removed 2026-10-08: both are India-only routes that middleware 404s
          // on this site (lib/edition/indiaOnlyRoutes.ts), and no href here may 404.
          { label: "Free contract scan", href: "/scan", desc: "Paste a contract, get a report" },
        ],
      },
      {
        title: "Learn",
        links: [
          { label: "Blog", href: "/blog", desc: "Legal AI, compliance and contracts" },
          { label: "FAQs", href: "/faqs", desc: "The questions we get most" },
          { label: "System status", href: "/status", desc: "Live API and database status" },
        ],
      },
      // Roadmap and Changelog return 2026-09-18 — both are becoming real pages
      // in the same change set (see src/app/roadmap and src/app/changelog).
      {
        title: "Product",
        links: [
          { label: "Roadmap", href: "/roadmap", desc: "What we are building next" },
          { label: "Changelog", href: "/changelog", desc: "Latest releases and updates" },
        ],
      },
    ],
    featured: {
      eyebrow: "Free forever",
      line: "A free contract scanner — paste a contract, get a risk report, no account required.",
      cta: { label: "Scan a contract free", href: "/scan" },
      secondary: { label: "Read the FAQs", href: "/faqs" },
    },
    footer: {
      link: { label: "Read the blog", href: "/blog", accent: true },
      tag: "Legal AI, compliance and contracts",
    },
  },

  // ── 5. COMPANY ────────────────────────────────────────────────────────────
  // "Founded in Delhi" used to sit in the footer tag at 2.3:1 contrast, which
  // is where provenance goes to die. It is the featured card's line now.
  {
    id: "company",
    label: "Company",
    hint: "About LexVio",
    activePrefixes: ["/about", "/why-lexvio", "/team", "/partners", "/careers", "/contact", "/press"],
    anchor: "right",
    columns: [
      {
        title: "Company",
        spotlight: true,
        links: [
          { label: "About LexVio", href: "/about", desc: "Who we are and why we built this" },
          { label: "Why LexVio", href: "/why-lexvio", desc: "What we do that a general AI does not" },
          { label: "Team", href: "/team", desc: "The people building LexVio" },
          { label: "Careers", href: "/careers", desc: "Open roles — remote and Delhi HQ" },
        ],
      },
      {
        title: "Connect",
        links: [
          { label: "Partners", href: "/partners", desc: "For law firms and integrators" },
          { label: "Contact", href: "/contact", desc: "Talk to a human" },
          { label: "Press", href: "/press", desc: "Coverage, releases and press kit" },
          { label: "Investors", href: "/investors", desc: "The thesis, and how to reach us" },
          { label: "Trust centre", href: "/trust", desc: "Security posture and certifications" },
        ],
      },
    ],
    featured: {
      eyebrow: "The company",
      line: "LexVio is built by Global Synapse Technologies, headquartered in Delhi, for legal teams everywhere.",
      cta: { label: "About LexVio", href: "/about" },
      secondary: { label: "Careers", href: "/careers" },
    },
    footer: {
      link: { label: "Book a demo", href: "/book-demo", accent: true },
      tag: "Delhi, India",
    },
  },
];
